/* import __COLOCATED_TEMPLATE__ from './standalone-access-level-selector.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type Admin from 'embercom/models/admin';

interface Signature {
  Args: {
    column: {
      widthStyle: number;
    };
    row: {
      teammate: Admin;
      isCurrentAdmin: boolean;
      roles: any;
    };
    tableActions: {};
  };
}

/* eslint-disable @intercom/intercom/no-bare-strings */
export const FULL_ACCESS_ROLE_NAME = 'Full Access';
export const VIEW_ONLY_ROLE_NAME = 'View Only';
export const NO_ACCESS_ROLE_NAME = 'No Access';
/* eslint-enable @intercom/intercom/no-bare-strings */

export default class StandaloneAccessLevelSelector extends Component<Signature> {
  @service declare permissionsMutatorService: $TSFixMe;
  @service declare appService: { app: { canUseCopilotSeatProvisioningOnStandalone: boolean } };

  get teammate() {
    return this.args.row.teammate;
  }

  get accessLevelItems() {
    return this.filteredRoles.map((role: any) => ({
      text: role.name,
      value: role.id,
      isDisabled: false,
      component: 'table-cells/teammate-list/standalone-access-level-selector-dropdown-item',
      componentAttrs: {
        role,
      },
    }));
  }

  get currentAccessLevelValue() {
    return this.teammate.currentAppPermissions.role?.id || this.fullAccessRole.id;
  }

  get currentAccessLevel() {
    return this.teammate.currentAppPermissions.role?.name || FULL_ACCESS_ROLE_NAME;
  }

  private get fullAccessRole() {
    return this.args.row.roles.find((role: any) => role.name === FULL_ACCESS_ROLE_NAME);
  }

  private get filteredRoles() {
    if (this.shouldShowNoAccessRole) {
      return this.sortedRoles;
    }

    return this.sortedRoles.filter((role: any) => role.name !== NO_ACCESS_ROLE_NAME);
  }

  private get shouldShowNoAccessRole() {
    return (
      this.appService.app.canUseCopilotSeatProvisioningOnStandalone ||
      this.currentAccessLevel === NO_ACCESS_ROLE_NAME
    );
  }

  private get sortedRoles() {
    return this.args.row.roles.toArray().sort((a: any, b: any) => {
      if (a.name === FULL_ACCESS_ROLE_NAME || b.name === NO_ACCESS_ROLE_NAME) {
        return -1;
      } else if (a.name === NO_ACCESS_ROLE_NAME || b.name === FULL_ACCESS_ROLE_NAME) {
        return 1;
      }

      return 0;
    });
  }

  @action async onAccessLevelChange(value: string) {
    if (this.teammate.currentAppPermissions.role_id !== value) {
      this.teammate.currentAppPermissions.role_id = value;

      await this.permissionsMutatorService.savePermission(
        this.teammate,
        this.teammate.currentAppPermissions,
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'TableCells::TeammateList::StandaloneAccessLevelSelector': typeof StandaloneAccessLevelSelector;
    'table-cells/teammate-list/standalone-access-level-selector': typeof StandaloneAccessLevelSelector;
  }
}
